import { Button, Icon, Input, Modal, Segmented, SwitchControl, useToast, type FormListItem } from '@elva/ui';
import { useEffect, useMemo, useState } from 'react';
import { extractApiErrorMessage } from '@/api/errors';
import { usePatientSearch } from '@/features/patients/hooks/usePatientSearch';
import {
  formatPatientSearchSubtitle,
  normalizePatientSearchResults,
} from '@/features/patients/adapters/patientAdapters';
import type { PatientSearchResult } from '@/features/patients/api/types';
import type { FormSendChannel } from '../api/types';
import { useSendForm } from '../hooks/useFormTemplates';

const SEARCH_DEBOUNCE_MS = 300;

interface SendFormDialogProps {
  form: FormListItem;
  onClose: () => void;
}

export function SendFormDialog({ form, onClose }: SendFormDialogProps) {
  const { toast } = useToast();
  const sendForm = useSendForm();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [dueAt, setDueAt] = useState('');
  const [note, setNote] = useState('');
  const [includeLink, setIncludeLink] = useState(true);
  // W21 — explicit staff-chosen delivery channel (Recall only chooses for triggered sends).
  const [channel, setChannel] = useState<FormSendChannel>('sms');

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setDebouncedQuery('');
      return;
    }
    const id = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [query]);

  const { data, isFetching } = usePatientSearch(debouncedQuery.length >= 2 ? debouncedQuery : undefined);
  const results = useMemo(() => normalizePatientSearchResults(data), [data]);
  const isSearchPending = query.trim().length >= 2 && query.trim() !== debouncedQuery;
  const isSearching = isSearchPending || isFetching;

  const handleSend = async () => {
    if (!patient) return;
    try {
      const res = await sendForm.mutateAsync({
        form_id: form.id,
        patient_id: patient.id,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        ...(note.trim() ? { note: note.trim() } : {}),
        include_access_link: includeLink,
        channel,
      });

      if (res.created.length === 0) {
        toast({
          title: 'Already sent',
          description: 'This patient already has an open assignment for this form.',
          variant: 'warning',
        });
        onClose();
        return;
      }

      const link = res.patient_access?.patient_url;
      if (includeLink && link) {
        try {
          await navigator.clipboard.writeText(link);
          toast({
            title: 'Form sent',
            description: 'Patient access link copied to clipboard.',
            variant: 'success',
          });
        } catch {
          toast({ title: 'Form sent', description: link, variant: 'success' });
        }
      } else {
        toast({ title: 'Form sent', variant: 'success' });
      }
      onClose();
    } catch (error) {
      toast({ title: 'Send failed', description: extractApiErrorMessage(error), variant: 'error' });
    }
  };

  return (
    <Modal
      title={`Send “${form.name}”`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft="send"
            disabled={!patient || sendForm.isPending}
            onClick={() => {
              void handleSend();
            }}
          >
            {sendForm.isPending ? 'Sending…' : 'Send form'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {patient ? (
          <div className="flex items-center justify-between rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[var(--ink-0)]">{patient.name}</p>
              <p className="font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
                {formatPatientSearchSubtitle(patient) || 'Patient'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPatient(null);
                setQuery('');
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <div>
            <Input
              label="Patient"
              icon="search"
              placeholder="Search patients…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
            {query.length >= 2 ? (
              <div className="mt-2 max-h-[180px] overflow-y-auto rounded-[var(--r-md)] border border-[var(--cream-2)]">
                {isSearching ? (
                  <p className="px-3 py-3 text-xs text-[var(--ink-4)]">Searching…</p>
                ) : results.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-[var(--ink-4)]">No patients found.</p>
                ) : (
                  results.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      onClick={() => {
                        setPatient(result);
                      }}
                      className="flex w-full items-center justify-between gap-2 border-b border-[var(--cream-2)] px-3 py-2 text-left last:border-b-0 hover:bg-[var(--cream-0)]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-[var(--ink-0)]">{result.name}</span>
                        <span className="block font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
                          {formatPatientSearchSubtitle(result) || 'Patient'}
                        </span>
                      </span>
                      <Icon kind="right" size={14} className="text-[var(--ink-4)]" />
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-[var(--ink-1)]">Send via</span>
          <Segmented
            options={[
              { value: 'sms', label: 'Text' },
              { value: 'email', label: 'Email' },
            ]}
            value={channel}
            onChange={(value) => {
              setChannel(value as FormSendChannel);
            }}
          />
        </div>

        <Input
          label="Due date (optional)"
          type="date"
          value={dueAt}
          onChange={(e) => {
            setDueAt(e.target.value);
          }}
        />

        <Input
          label="Note (optional)"
          placeholder="Add a note for the patient…"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />

        <label className="flex items-center justify-between gap-3">
          <span className="text-sm text-[var(--ink-1)]">Include patient access link</span>
          <SwitchControl checked={includeLink} onChange={setIncludeLink} />
        </label>
      </div>
    </Modal>
  );
}
